/* ============================================================
   Mega menu — Desktop only (>= 1024px)
   ------------------------------------------------------------
   Handles hover dropdown panels, tree switching, focus management.
   Completely separate from mobile drawer logic.
   ============================================================ */

class MegaMenuDesktop extends HTMLElement {
  constructor() {
    super();

    this.menuParentItems = this.querySelectorAll(
      ".wt-page-nav-mega__item--parent",
    );
    this.classParentActiveDesk = "dropdown-opened";
    this.classBodyActiveDesk = "dropdown-open-desk";
    this.desktopBreakpoint = 1024;
    this.isDesktop = () =>
      window.matchMedia(`(min-width: ${this.desktopBreakpoint}px)`).matches;
  }

  connectedCallback() {
    // The desktop nav tree is display:none below the breakpoint (see
    // critical.css .wt-header__nav-teaser--full-menu), so having listeners
    // bound on mobile is harmless. Bind immediately so a viewport that
    // starts narrow (or a rotated device) still gets a working dropdown
    // after crossing to >=1024 without a page reload.
    if (!this._inited) {
      this._inited = true;
      this.init();
    }

    // If a panel happens to be open when the viewport drops below the
    // breakpoint, collapse it and drop the opaque-header body class so the
    // mobile view starts from a clean state.
    this._mq = window.matchMedia(`(min-width: ${this.desktopBreakpoint}px)`);
    this._onMqChange = (e) => {
      if (!e.matches) this.closeAll();
    };
    if (this._mq.addEventListener) {
      this._mq.addEventListener("change", this._onMqChange);
    } else if (this._mq.addListener) {
      this._mq.addListener(this._onMqChange);
    }
  }

  closeAll() {
    this.menuParentItems.forEach((item) => {
      if (item._megaCloseTimer) {
        clearTimeout(item._megaCloseTimer);
        item._megaCloseTimer = null;
      }
      item.classList.remove(this.classParentActiveDesk, "submenu--left");
    });
    document.body.classList.remove(this.classBodyActiveDesk);
  }

  disconnectedCallback() {
    if (this._mq && this._onMqChange) {
      if (this._mq.removeEventListener) {
        this._mq.removeEventListener("change", this._onMqChange);
      } else if (this._mq.removeListener) {
        this._mq.removeListener(this._onMqChange);
      }
    }
    if (this._onEsc) document.removeEventListener("keydown", this._onEsc);
  }

  initTreeSwitching() {
    // Panels that switch panes on the left: the tree layout (left = menu
    // children) and links-left-cards-right (left = link_group headings, used
    // when the panel has no second-level menu children).
    const panels = this.querySelectorAll(
      ".mega-menu__panel--tree, .mega-menu__panel--links-left-cards-right",
    );

    panels.forEach((panel) => {
      const items = panel.querySelectorAll(".mega-menu__tree-item");
      if (!items.length) return;

      const stage = panel.querySelector(".mega-menu__stage");
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      const paneOf = (item) =>
        panel.querySelector(
          `.mega-menu__grid-wrap[data-group-id="${item.dataset.groupId}"]`,
        );

      // Only the ACTIVE tree item reveals its card-group anchors. Every other
      // item stays collapsed, so the tree reads as a click-to-open accordion
      // with the first item open on load — listing every item's anchors at
      // once made the whole tree look permanently expanded.
      const syncSublist = (item) => {
        const host = item.querySelector("[data-sublist]");
        if (!host) return;
        host.hidden =
          !item.classList.contains("active") || !host.children.length;
      };

      // A pane's card-group headers double as anchors in the left tree. Built
      // from the DOM so the card binding rules live in one place (Liquid).
      const buildSublist = (item) => {
        const host = item.querySelector("[data-sublist]");
        if (!host) return;
        const pane = paneOf(item);
        const headers = pane
          ? pane.querySelectorAll(".mega-menu__card-group-header")
          : [];
        host.textContent = "";
        if (headers.length) {
          headers.forEach((header, index) => {
            const label = header.textContent.trim();
            if (!label) return;
            const li = document.createElement("li");
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "mega-menu__tree-sublink";
            btn.dataset.cardGroupIndex = String(index);
            btn.textContent = label;
            li.appendChild(btn);
            host.appendChild(li);
          });
        }
        syncSublist(item);
      };

      const scrollToCardGroup = (item, index) => {
        if (!stage) return;
        const pane = paneOf(item);
        const header = pane?.querySelectorAll(".mega-menu__card-group-header")[
          index
        ];
        if (!header) return;
        const top =
          header.getBoundingClientRect().top -
          stage.getBoundingClientRect().top +
          stage.scrollTop;
        stage.scrollTo({
          top: Math.max(0, top - 12),
          behavior: reduceMotion ? "auto" : "smooth",
        });
      };

      const activate = (targetItem, options = {}) => {
        items.forEach((item) => {
          const isActive = item === targetItem;
          item.classList.toggle("active", isActive);
          const btn = item.querySelector(".mega-menu__tree-title");
          if (btn)
            btn.setAttribute("aria-expanded", isActive ? "true" : "false");
          syncSublist(item);
        });
        panel.querySelectorAll(".mega-menu__grid-wrap").forEach((grid) => {
          if (grid.dataset.groupId === targetItem.dataset.groupId) {
            grid.removeAttribute("hidden");
            // Trigger the enter transition on the next frame
            requestAnimationFrame(() => {
              grid.classList.add("is-entering");
              requestAnimationFrame(() => {
                grid.classList.remove("is-entering");
              });
            });
          } else {
            grid.setAttribute("hidden", "");
          }
        });
        // The stage is a scroll container; carrying the previous pane's scroll
        // offset over slices the incoming pane's first row at the top.
        if (stage && !options.keepScroll) stage.scrollTop = 0;
      };

      const sublistHandler = (item) => (event) => {
        const link = event.target.closest(".mega-menu__tree-sublink");
        if (!link || !item.contains(link)) return;
        activate(item, { keepScroll: true });
        scrollToCardGroup(item, Number(link.dataset.cardGroupIndex));
      };

      items.forEach((item) => {
        buildSublist(item);
        const btn = item.querySelector(".mega-menu__tree-title");
        const host = item.querySelector("[data-sublist]");
        if (host) host.addEventListener("click", sublistHandler(item));
        if (!btn) return;
        // Click only. Hover used to swap the pane as well, which made the
        // tree feel like it expanded on its own the moment the cursor
        // travelled to the stage.
        btn.addEventListener("click", () => activate(item));
      });

      // Keep the anchors in sync when a pane re-renders (variant switch in the
      // theme editor). Observe the stage only — watching the tree would make
      // buildSublist's own writes re-trigger the observer.
      if (stage && !panel._megaSublistObserver && "MutationObserver" in window) {
        panel._megaSublistObserver = new MutationObserver(() => {
          items.forEach((item) => buildSublist(item));
        });
        panel._megaSublistObserver.observe(stage, {
          childList: true,
          subtree: true,
        });
      }
    });
  }

  init() {
    const {
      menuParentItems,
      classBodyActiveDesk,
      classParentActiveDesk,
    } = this;

    this.initTreeSwitching();

    const toggleSubmenuDesk = (item) => {
      const headerElement = document.querySelector(".wt-header");
      const headerHeight = headerElement.offsetHeight;
      headerElement.style.setProperty(
        "--mega-menu-top-position",
        `${headerHeight}px`,
      );

      const parentLink = item.querySelector('a[data-menu-level="1"]');
      const submenuLinks = parentLink.nextElementSibling.querySelectorAll(
        'a[data-menu-level="2"],a[data-menu-level="3"]',
      );
      if (this.isDesktop()) {
        setTabindex(
          submenuLinks,
          item.classList.contains(classParentActiveDesk) ? "0" : "-1",
        );
        parentLink.setAttribute(
          "aria-expanded",
          item.classList.contains(classParentActiveDesk) ? "true" : "false",
        );
      }
    };

    const leftSubmenuClass = "submenu--left";
    const closeDelay = 180;

    menuParentItems.forEach((item) => {
      const removeBodyClassIfNoItemHovered = () => {
        const anyItemStillActive = document.querySelector(
          `.${classParentActiveDesk}`,
        );
        if (!anyItemStillActive) {
          document.body.classList.remove(classBodyActiveDesk);
        }
      };

      const closeItem = () => {
        item._megaCloseTimer = null;
        item.classList.remove(classParentActiveDesk);
        item.classList.remove(leftSubmenuClass);
        toggleSubmenuDesk(item);
        removeBodyClassIfNoItemHovered();
      };

      addEventListeners(item, ["mouseover", "focusin"], () => {
        if (item._megaCloseTimer) {
          clearTimeout(item._megaCloseTimer);
          item._megaCloseTimer = null;
        }
        item.classList.remove("dropdown-switched");

        if (!item.classList.contains(classParentActiveDesk)) {
          // Switching items closes the previous panel immediately
          menuParentItems.forEach((other) => {
            if (other === item) return;
            if (other._megaCloseTimer) {
              clearTimeout(other._megaCloseTimer);
              other._megaCloseTimer = null;
            }
            if (other.classList.contains(classParentActiveDesk)) {
              other.classList.remove(classParentActiveDesk);
              other.classList.remove(leftSubmenuClass);
              // Mark for instant visual hide so the old panel cannot
              // cross-fade/overlap the newly opened one.
              other.classList.add("dropdown-switched");
              toggleSubmenuDesk(other);
            }
          });

          document.body.classList.add(classBodyActiveDesk);
          item.classList.add(classParentActiveDesk);

          // Compact simple card alignment is pure CSS: the simple parent
          // li is position:relative and the wrapper anchors to it. JS only
          // flips the anchor when the card would overflow the viewport.
          // Defer to rAF so we measure the settled open layout.
          const simpleWrapper = item.querySelector(
            ".wt-page-nav-mega__sublist__wrapper--simple",
          );
          if (simpleWrapper) {
            simpleWrapper.style.left = "";
            simpleWrapper.style.right = "";
            const isRtl =
              document.documentElement.getAttribute("dir") === "rtl";
            requestAnimationFrame(() => {
              const rect = simpleWrapper.getBoundingClientRect();
              if (isRtl && rect.left < 16) {
                simpleWrapper.style.left = "0";
                simpleWrapper.style.right = "auto";
              } else if (!isRtl && rect.right > window.innerWidth - 16) {
                simpleWrapper.style.left = "auto";
                simpleWrapper.style.right = "0";
              }
            });
          }

          const xCoords = item.getBoundingClientRect().x;
          const windowWidth = window.innerWidth;
          const isElementInSecondHalfOfWindow = xCoords > windowWidth / 2;
          item.classList.toggle(
            leftSubmenuClass,
            isElementInSecondHalfOfWindow,
          );

          // Reopening the panel must not inherit the in-pane scroll offset from
          // the previous visit, or the first card row opens already sliced off.
          const panel = item.querySelector(
            ".mega-menu__panel--tree, .mega-menu__panel--links-left-cards-right",
          );
          const stage = panel?.querySelector(".mega-menu__stage");
          if (stage) stage.scrollTop = 0;

          toggleSubmenuDesk(item);
        }
      });

      addEventListeners(item, ["mouseout", "focusout"], () => {
        if (item._megaCloseTimer) clearTimeout(item._megaCloseTimer);
        item._megaCloseTimer = setTimeout(() => {
          if (item.matches(":hover")) {
            item._megaCloseTimer = null;
            return;
          }
          const active = document.activeElement;
          if (active && item.contains(active)) {
            item._megaCloseTimer = null;
            return;
          }
          closeItem();
        }, closeDelay);
      });
    });

    // Esc key closes any open desktop dropdown. Stored on the instance so
    // disconnectedCallback can remove it (theme-editor section reloads
    // otherwise stack one document-level listener per reload).
    this._onEsc = (e) => {
      if (e.key !== "Escape") return;
      const opened = document.querySelector(`.${classParentActiveDesk}`);
      if (!opened) return;
      if (opened._megaCloseTimer) {
        clearTimeout(opened._megaCloseTimer);
        opened._megaCloseTimer = null;
      }
      opened.classList.remove(classParentActiveDesk);
      opened.classList.remove("submenu--left");
      const trigger = opened.querySelector('a[data-menu-level="1"]');
      if (trigger) trigger.setAttribute("aria-expanded", "false");
      const submenuLinks = opened.querySelectorAll(
        'a[data-menu-level="2"],a[data-menu-level="3"]',
      );
      setTabindex(submenuLinks, "-1");
      document.body.classList.remove(classBodyActiveDesk);
    };
    document.addEventListener("keydown", this._onEsc);
  }
}

customElements.define("mega-menu-desktop", MegaMenuDesktop);
